"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useInView } from "motion/react";
import {
  triggerGoogleTagManagerCustomEvent,
  GoogleTagManagerCustomEvents,
} from "@marketing-sites/analytics/google-tag-manager";
import { triggerAnalyticsEvent } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingAction, TrackingType } from "@marketing-sites/analytics/types";
import { recordEmailSubmission } from "@marketing-sites/analytics/email-submission";
import { pickGtmFields } from "@marketing-sites/analytics/gtm-form-fields";
import * as Sentry from "@sentry/nextjs";
import { cn } from "@marketing-sites/utils/cn";
import {
  customizeFormFields,
  setupEmailBlurValidation,
  getVisibleFieldNames,
  removeMarketoFormStyles,
  reapplyOnRerender,
  recordMarketoCustomCss,
} from "./dom-customizations";
import { HeroFormSuccess } from "./success-message";
import "./native-marketo-form.css";

const SENTRY_FORM_TYPE = "native-marketo";
const SENTRY_ERROR_TYPES = {
  SCRIPT_LOAD_FAILURE: "script_load_failure",
  MKTOFORMS2_UNAVAILABLE: "mktoforms2_unavailable",
  CUSTOM_CSS_UNRECORDED: "custom_css_unrecorded",
} as const;

declare global {
  interface Window {
    MktoForms2?: {
      loadForm: (
        baseUrl: string,
        munchkinId: string,
        formId: number | string,
        callback?: (form: MktoForm) => void,
      ) => void;
      whenReady: (callback: (form: MktoForm) => void) => void;
    };
  }
}

interface MktoForm {
  onSuccess: (
    callback: (values: Record<string, unknown>, followUpUrl: string) => boolean,
  ) => void;
  getFormElem: () => { get: (index: number) => HTMLElement };
}

interface FormImpressionData {
  formType: string;
  text: string;
  location: string;
}

interface NativeMarketoFormProps {
  baseUrl: string;
  munchkinId: string;
  formId: number | string;
  formPostSubmitAction?: "showMessage" | "redirect" | "downloadPdf";
  successTitleText?: string;
  successBodyText?: string;
  successRedirectUrl?: string;
  pdfUrl?: string;
  pdfFilename?: string;
  onLoadError?: () => void;
  formImpressionData?: FormImpressionData;
}

function appendForms2Script(src: string) {
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
  return script;
}

/** Only show "Loading form…" after this delay — fast loads never flash the text */
const LOADING_TEXT_DELAY_MS = 400;

export function NativeMarketoForm({
  baseUrl,
  munchkinId,
  formId,
  formPostSubmitAction,
  successTitleText = "Thank you!",
  successBodyText = "We will be in touch shortly.",
  successRedirectUrl,
  pdfUrl,
  pdfFilename,
  onLoadError,
  formImpressionData,
}: NativeMarketoFormProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingText, setShowLoadingText] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleFields, setVisibleFields] = useState<string[] | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const formRequestedRef = useRef(false);
  const isInView = useInView(formRef, { once: true });
  const impressionFiredRef = useRef(false);

  // Resolve effective action: use explicit field if set, otherwise infer from data (legacy docs)
  const effectiveAction = useMemo(
    () =>
      formPostSubmitAction ||
      (successRedirectUrl
        ? "redirect"
        : pdfUrl
          ? "downloadPdf"
          : "showMessage"),
    [formPostSubmitAction, successRedirectUrl, pdfUrl],
  );

  // Delayed loading text — avoids flashing "Loading form…" on fast connections
  useEffect(() => {
    if (!isLoading) {
      setShowLoadingText(false);
      return;
    }
    const timer = setTimeout(
      () => setShowLoadingText(true),
      LOADING_TEXT_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handleSuccess = useCallback(
    (values: Record<string, unknown>) => {
      setIsSubmitted(true);

      try {
        // Marketo native forms return PascalCase keys (Email, FirstName, etc.).
        // recordEmailSubmission handles both Email/email for the primary lookup.
        recordEmailSubmission(formId, values);
        triggerGoogleTagManagerCustomEvent(
          GoogleTagManagerCustomEvents.marketoFormSubmitted,
          {
            "marketo-form-id": formId,
            formType: "nativeMarketo",
            ...pickGtmFields(values),
          },
        );
      } catch (error) {
        console.error("Failed to record form submission:", error);
      }

      if (effectiveAction === "redirect" && successRedirectUrl) {
        window.location.href = successRedirectUrl;
      } else if (effectiveAction === "downloadPdf" && pdfUrl) {
        fetch(pdfUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`,
              );
            }
            return response.blob();
          })
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = pdfFilename || "download.pdf";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch((err) => {
            console.error("Failed to download PDF:", err);
            window.location.href = pdfUrl;
          });
      }
    },
    [formId, effectiveAction, successRedirectUrl, pdfUrl, pdfFilename],
  );

  // Fire form impression analytics when the form is both loaded and in view
  useEffect(() => {
    if (isInView && visibleFields && !impressionFiredRef.current) {
      impressionFiredRef.current = true;
      triggerAnalyticsEvent({
        action: TrackingAction.Visible,
        type: TrackingType.Element,
        text: formImpressionData?.text ?? "DRLP Hero Form",
        analyticsId: "evt_drlp_hero_native_marketo_form_impression",
        location: formImpressionData?.location ?? "section-drlp-hero",
        metadata: {
          formType: formImpressionData?.formType ?? "progressive",
          formId,
          visibleFields,
          fieldCount: visibleFields.length,
        },
      });
    }
  }, [isInView, visibleFields, formId, formImpressionData]);

  // Munchkin is handled globally by GTM which sets the _mkto_trk cookie on page
  // load. This component only needs to load forms2.min.js and the form itself.
  useEffect(() => {
    if (formRequestedRef.current) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    const scriptSrc = `${baseUrl}/js/forms2/js/forms2.min.js`;

    const loadForm = () => {
      if (!window.MktoForms2) {
        setError("Failed to load Marketo Forms");
        setIsLoading(false);
        Sentry.captureException(
          new Error(
            `[native-marketo-form] MktoForms2 unavailable after script load for form ${formId}`,
          ),
          {
            tags: {
              formType: SENTRY_FORM_TYPE,
              errorType: SENTRY_ERROR_TYPES.MKTOFORMS2_UNAVAILABLE,
            },
            extra: { formId, baseUrl },
          },
        );
        onLoadError?.();
        return;
      }

      formRequestedRef.current = true;
      if (!recordMarketoCustomCss(window.MktoForms2)) {
        Sentry.captureMessage(
          `[native-marketo-form] MktoForms2.processForm missing; form ${formId} keeps its Marketo custom CSS`,
          {
            level: "warning",
            tags: {
              formType: SENTRY_FORM_TYPE,
              errorType: SENTRY_ERROR_TYPES.CUSTOM_CSS_UNRECORDED,
            },
            extra: { formId },
          },
        );
      }
      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, (form) => {
        setIsLoading(false);

        const formEl = form.getFormElem().get(0);
        const customize = () => {
          removeMarketoFormStyles(formEl);
          customizeFormFields(formEl);
          setupEmailBlurValidation(formEl, signal);
        };
        customize();
        reapplyOnRerender(formEl, customize, signal);
        setVisibleFields(getVisibleFieldNames(formEl));

        form.onSuccess((values) => {
          handleSuccess(values);
          return false; // Prevent Marketo's default redirect
        });
      });
    };

    // Check if MktoForms2 is already loaded (e.g., from another instance)
    if (window.MktoForms2) {
      loadForm();
      return () => {
        abortController.abort();
      };
    }

    // Reuse a pending tag rather than appending another: in dev, Strict Mode
    // re-runs this effect before the first run's script has loaded.
    const forms2Script =
      document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`) ??
      appendForms2Script(scriptSrc);

    const handleScriptError = () => {
      forms2Script.remove();
      setError("Failed to load Marketo Forms script");
      setIsLoading(false);
      Sentry.captureException(
        new Error(
          `[native-marketo-form] Failed to load forms2.min.js for form ${formId}`,
        ),
        {
          tags: {
            formType: SENTRY_FORM_TYPE,
            errorType: SENTRY_ERROR_TYPES.SCRIPT_LOAD_FAILURE,
          },
          extra: { formId, baseUrl, scriptSrc },
        },
      );
      onLoadError?.();
    };

    forms2Script.addEventListener("load", loadForm, { signal });
    forms2Script.addEventListener("error", handleScriptError, { signal });

    return () => {
      abortController.abort();
    };
  }, [baseUrl, munchkinId, formId, handleSuccess, onLoadError]);

  if (isSubmitted) {
    return (
      <HeroFormSuccess
        title={successTitleText}
        body={successBodyText}
        pdfUrl={effectiveAction === "downloadPdf" ? pdfUrl : undefined}
      />
    );
  }

  return (
    <div
      ref={formRef}
      className="native-marketo-form w-full"
      data-analytics-region="Form: DRLP Hero"
      data-analytics-id="form_drlp_hero_native_marketo_form"
    >
      {error && !onLoadError ? (
        <div className="text-current/70 flex items-center justify-center py-8">
          <p className="text-body-sm">{error}</p>
        </div>
      ) : !error ? (
        <div className="grid [&>*]:col-start-1 [&>*]:row-start-1">
          {/* Loading indicator — delayed so fast loads never flash it, fades out smoothly */}
          <div
            className={cn(
              "flex min-h-[200px] items-center justify-center py-8 transition-opacity duration-300",
              showLoadingText && isLoading
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
            aria-hidden={!isLoading}
          >
            <p className="text-body-sm text-current/60">Loading form...</p>
          </div>
          {/* Form — always in DOM for Marketo to find, fades in when loaded.
             Opacity lives on a wrapper div because Marketo imperatively adds
             the "mktoForm" class to <form>; putting className on the <form>
             lets React overwrite it on re-render, stripping mktoForm. */}
          <div
            className={cn(
              "transition-opacity duration-300",
              isLoading ? "opacity-0" : "opacity-100",
            )}
          >
            <form id={`mktoForm_${formId}`} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
